// @ts-check
// ── What makes up a number: the card a chart's bar or point opens ────────────
//
// Clayton, 10/30: "click on a bar or point in time on the line graph and see the actual breakdown of what makes up that
// number (specific clients, their spend, etc. ...) remember we should always be able to drill down if we want to. our
// platform is build on simple but powerful."
//
// The server lists the very records behind the number (netlify/functions/coach-dashboard-detail.js, from
// _shared/dashboard/detail.js), so the list always adds up to the bar. Money is grouped by person, biggest first, each
// opening to their payments; clients are grouped by type, with who joined and who left that month first. A name opens
// the client's profile. A card in the middle of the page, like the CRM's other pop-ups (Clayton chose it over a drawer
// from the side, 10/30); a long list scrolls inside it and the total stays at its foot, every amount lined up with it.

import { callFunction } from '../api.js';
import { escHtml } from '../utils.js';
import { money, plural, shortDay, monthOf } from './words.js';
import { ICON } from './tiles.js';

/** @typedef {{ what: 'cash'|'refunds'|'renewals', from: string, to: string } | { what: 'active'|'mrr'|'ltv'|'cac', day: string }} Ask */
/** @typedef {{ eyebrow: string, title: string, total: string }} Head */

/** The element that had the keyboard before the card opened, to give it back. @type {HTMLElement|null} */
let back = null;
/** Whether the card was opened from the keyboard: only then does closing it hand the keyboard back. */
let giveBack = false;
/** The coach's last way in: a key, or the mouse or a finger. */
let byKey = false;
// ⚠️ A CLICKED CHART IS NOT FOCUSED AGAIN ON CLOSE (Clayton 10/30, the blue ring): focus handed back to a chart after a
// click drew the browser's ring around it and opened its last month's tooltip. Only a keyboard user gets it back.
if (typeof document !== 'undefined') {
  document.addEventListener('keydown', () => { byKey = true; }, true);
  document.addEventListener('pointerdown', () => { byKey = false; }, true);
}
/** The ask on screen: an answer for an older one is dropped. */
let asked = 0;

function ensure() {
  let d = /** @type {HTMLElement|null} */ (document.getElementById('dv-drill'));
  if (d) return d;
  const shade = document.createElement('div');
  shade.id = 'dv-drill-shade';
  shade.className = 'dv-drill-shade';
  d = document.createElement('aside');
  d.id = 'dv-drill';
  d.className = 'dv-drill';
  d.setAttribute('role', 'dialog');
  d.setAttribute('aria-modal', 'true');
  d.setAttribute('aria-labelledby', 'dv-drill-t');
  document.body.append(shade, d);
  shade.addEventListener('click', closeDrill);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && d && d.classList.contains('on')) { e.preventDefault(); closeDrill(); } });
  d.addEventListener('click', (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    if (t.closest('[data-drill-close]')) { closeDrill(); return; }
    const tab = /** @type {HTMLElement|null} */ (t.closest('[data-drill-tab]'));
    if (tab && d) {
      d.querySelectorAll('[data-drill-tab]').forEach((b) => b.setAttribute('aria-selected', String(b === tab)));
      d.querySelectorAll('[data-drill-pane]').forEach((p) => { /** @type {HTMLElement} */ (p).hidden = /** @type {HTMLElement} */ (p).dataset.drillPane !== tab.dataset.drillTab; });
      const b = /** @type {HTMLElement|null} */ (d.querySelector('.dv-drill-b'));
      if (b) b.scrollTop = 0;
      return;
    }
    const c = /** @type {HTMLElement|null} */ (t.closest('[data-client]'));
    if (c) {
      const open = /** @type {any} */ (window).showClientProfile;
      if (typeof open === 'function') { closeDrill(); open(c.dataset.client); }
    }
  });
  return d;
}

export function closeDrill() {
  const d = document.getElementById('dv-drill');
  if (!d || !d.classList.contains('on')) return;
  d.classList.remove('on');
  document.getElementById('dv-drill-shade')?.classList.remove('on');
  asked++;
  const inside = /** @type {HTMLElement|null} */ (document.activeElement);
  if (giveBack && back) back.focus();
  else if (inside && d.contains(inside)) inside.blur();
  back = null;
}

/** @param {Head} h @param {string} sub @param {string} body @param {string} [foot] */
const frame = (h, sub, body, foot = '') => `<header class="dv-drill-h">
    <div><div class="dv-drill-eye">${escHtml(h.eyebrow)}</div><h3 id="dv-drill-t">${escHtml(h.title)}</h3>
      <div class="dv-drill-total">${escHtml(h.total)}</div>${sub ? `<div class="dv-drill-sub">${escHtml(sub)}</div>` : ''}</div>
    <button type="button" class="dv-drill-x" data-drill-close aria-label="Close">${ICON.close}</button>
  </header><div class="dv-drill-b">${body}</div>${foot ? `<footer class="dv-drill-f">${foot}</footer>` : ''}`;

/** A name that opens the client, or just the name for someone FitOps has no client for. @param {{ clientId: string|null, name: string }} p */
const who = (p) => (p.clientId ? `<button type="button" class="dv-link dv-drill-name" data-client="${escHtml(p.clientId)}">${escHtml(p.name)}</button>` : `<span class="dv-drill-name">${escHtml(p.name)}</span>`);
/** The person's initials in a circle, as the CRM's lists show a client without a photo. @param {string} name */
const av = (name) => `<span class="dv-av" aria-hidden="true">${escHtml(name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?')}</span>`;

/** A share of the number in words: "Under 1%" for a sliver. @param {number} p */
const pct = (p) => (p > 0 && p < 1 ? 'Under 1%' : `${Math.round(p)}%`);
/** A day, with its year when it is not this one. @param {string} d */
const dayWords = (d) => shortDay(d, Number(d.slice(0, 4)) !== new Date().getFullYear());
/** The total, in the same columns as the rows above it, so every amount lines up with it (Clayton, 10/30). @param {string} label @param {string} value */
const sumRow = (label, value) => `<div class="dv-drill-sum"><i></i><span>${escHtml(label)}</span><b>${escHtml(value)}</b><i></i></div>`;
/** "a, b and c". @param {string[]} xs */
const andList = (xs) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

/**
 * Clients, a row each that opens their profile: initials, name, a line under it, a number on the right.
 * @param {Array<{ clientId: string|null, name: string, line: string, amount: string, note?: string, share?: number }>} xs
 */
function clientRows(xs) {
  return `<ul class="dv-drill-list">${xs.map((x) => {
    const head = `${av(x.name)}<span class="dv-drill-who"><span class="dv-drill-n">${escHtml(x.name)}</span><small>${escHtml(x.line)}</small>${x.share ? `<i class="dv-share"><i style="width:${x.share.toFixed(1)}%"></i></i>` : ''}</span>
        <span class="dv-drill-amt"><b>${escHtml(x.amount)}</b>${x.note ? `<small>${escHtml(x.note)}</small>` : ''}</span>`;
    return `<li class="dv-drill-g">${x.clientId
      ? `<button type="button" class="dv-drill-row" data-client="${escHtml(x.clientId)}" aria-label="${escHtml(`${x.name}${x.amount ? `, ${x.amount}` : ''}. Open their profile`)}">${head}${ICON.go}</button>`
      : `<div class="dv-drill-row">${head}<i></i></div>`}</li>`;
  }).join('')}</ul>`;
}

/**
 * Money, by person, biggest first: a person with one charge is one row that opens their profile; several charges (a
 * monthly payment, a session pack, apparel) open to each one, newest first.
 * @param {Array<{ day: string, amount: number, clientId: string|null, name: string, what: string }>} items
 * @param {[string, string]} [noun] what one item is called, and several
 */
function peopleRows(items, noun = ['payment', 'payments']) {
  /** @type {Map<string, { clientId: string|null, name: string, total: number, items: typeof items }>} */
  const by = new Map();
  for (const i of items) {
    const k = i.clientId || `name:${i.name}`;
    const g = by.get(k) || { clientId: i.clientId, name: i.name, total: 0, items: [] };
    g.total += i.amount; g.items.push(i); by.set(k, g);
  }
  const groups = [...by.values()].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
  const total = items.reduce((t, i) => t + i.amount, 0);
  const day = dayWords;
  // When they paid, rather than "Card payment" on every row: one payment's day and what it was, or the span of several
  const when = (/** @type {typeof groups[number]} */ g) => {
    if (g.items.length === 1) return `${day(g.items[0].day)} · ${g.items[0].what}`;
    const first = g.items[g.items.length - 1].day, last = g.items[0].day;
    return `${plural(g.items.length, noun[0], noun[1])} · ${first === last ? day(first) : first.slice(0, 7) === last.slice(0, 7) ? `${day(first)} to ${Number(last.slice(8))}` : `${day(first)} to ${day(last)}`}`;
  };
  // Each person's share of the number, as a word and a thin bar: the biggest payers stand out at a glance
  const share = (/** @type {number} */ t) => (total > 0 && t > 0 ? (t / total) * 100 : 0);
  // ⚠️ ONE CHARGE DOES NOT OPEN (Clayton 10/30: opening it only repeated the row): its row already says when and what,
  // and clicking it opens the client. Several charges (a monthly payment, a session pack, apparel) open to each one.
  const head = (/** @type {typeof groups[number]} */ g, /** @type {number} */ p, /** @type {string} */ end) => `${av(g.name)}<span class="dv-drill-who"><span class="dv-drill-n">${escHtml(g.name)}</span><small>${escHtml(when(g))}</small>${p ? `<i class="dv-share"><i style="width:${p.toFixed(1)}%"></i></i>` : ''}</span>
        <span class="dv-drill-amt"><b class="${g.total < 0 ? 'is-neg' : ''}">${money(g.total)}</b>${p ? `<small>${pct(p)}</small>` : ''}</span>${end}`;
  const html = `<ul class="dv-drill-list">${groups.map((g) => {
    const p = share(g.total);
    if (g.items.length === 1) {
      return `<li class="dv-drill-g">${g.clientId
        ? `<button type="button" class="dv-drill-row" data-client="${escHtml(g.clientId)}" aria-label="${escHtml(`${g.name}, ${money(g.total)}. Open their profile`)}">${head(g, p, ICON.go)}</button>`
        : `<div class="dv-drill-row">${head(g, p, '<i></i>')}</div>`}</li>`;
    }
    return `<li><details class="dv-drill-g">
      <summary>${head(g, p, ICON.chev)}</summary>
      <ul class="dv-drill-pays">${g.items.map((i) => `<li><i></i><span>${day(i.day)}<em>${escHtml(i.what)}</em></span><b class="${i.amount < 0 ? 'is-neg' : ''}">${money(i.amount)}</b><i></i></li>`).join('')}</ul>
      ${g.clientId ? `<div class="dv-drill-open"><button type="button" class="dv-link" data-client="${escHtml(g.clientId)}">Open ${escHtml(g.name.split(' ')[0])}'s profile${ICON.go}</button></div>` : ''}
    </details></li>`;
  }).join('')}</ul>`;
  return { html, people: groups.length };
}

/** @param {{ total: number, items: Array<{ day: string, amount: number, clientId: string|null, name: string, what: string }> }} A */
function cashBody(A) {
  if (!A.items.length) return { sub: 'Nothing came in', body: '<p class="dv-drill-none">No payments in these dates.</p>', foot: '' };
  const r = peopleRows(A.items);
  const pays = A.items.filter((i) => i.amount > 0).length, back = A.items.length - pays;
  const sub = `${plural(pays, 'payment', 'payments')}${back ? ` and ${plural(back, 'refund', 'refunds')}` : ''} from ${plural(r.people, 'person', 'people')}`;
  return { sub, body: r.html, foot: sumRow('Total', money(A.total)) };
}

/** Money given back in a month, by person. @param {{ total: number, items: Array<{ day: string, amount: number, clientId: string|null, name: string, what: string }> }} A */
function refundsBody(A) {
  if (!A.items.length) return { sub: 'Nothing given back', body: '<p class="dv-drill-none">No refunds in these dates.</p>', foot: '' };
  const r = peopleRows(A.items, ['refund', 'refunds']);
  const disputes = A.items.filter((i) => i.what === 'Dispute').length, refunds = A.items.length - disputes;
  const sub = `${andList([refunds ? plural(refunds, 'refund', 'refunds') : '', disputes ? plural(disputes, 'dispute', 'disputes') : ''].filter(Boolean))} to ${plural(r.people, 'person', 'people')}`;
  return { sub, body: r.html, foot: sumRow('Total', money(A.total)) };
}

/** MRR on a day, client by client, biggest first. @param {{ total: number, clients: Array<{ clientId: string|null, name: string, amount: number, what: string }> }} A */
function mrrBody(A) {
  if (!A.clients.length) return { sub: 'Nobody on a monthly plan', body: '<p class="dv-drill-none">No client was on a monthly plan or continuity on this day.</p>', foot: '' };
  const share = (/** @type {number} */ n) => (A.total > 0 && n > 0 ? (n / A.total) * 100 : 0);
  return {
    sub: `${plural(A.clients.length, 'client pays', 'clients pay')} every month`,
    body: clientRows(A.clients.map((c) => ({ clientId: c.clientId, name: c.name, line: c.what, amount: money(c.amount), note: pct(share(c.amount)), share: share(c.amount) }))),
    foot: sumRow('MRR', money(A.total)),
  };
}

/** Programs ending in a week, soonest first. @param {{ monthly: number, programs: Array<{ clientId: string|null, name: string, end: string, stepLabel: string, monthly: number }> }} A */
function renewalsBody(A) {
  if (!A.programs.length) return { sub: 'Nothing ends this week', body: '<p class="dv-drill-none">No programs end in these dates.</p>', foot: '' };
  return {
    sub: A.programs.length === 1 ? `${money(A.monthly)} a month` : `${money(A.monthly)} a month between them`,
    body: clientRows(A.programs.map((p) => ({ clientId: p.clientId, name: p.name, line: `Ends ${dayWords(p.end)} · ${p.stepLabel}`, amount: p.monthly ? money(p.monthly) : '', note: p.monthly ? 'a month' : '' }))),
    foot: sumRow('Total a month', money(A.monthly)),
  };
}

/**
 * Cost to win a client over 90 days: the ad money, and every client who joined, from ads first.
 * @param {{ day: string, from: string, spend: number, paid: number|null, blended: number|null, clients: Array<{ clientId: string|null, name: string, joined: string, fromAds: boolean }> }} A
 */
function cacBody(A) {
  const ads = A.clients.filter((c) => c.fromAds), other = A.clients.filter((c) => !c.fromAds);
  const list = (/** @type {typeof ads} */ xs) => clientRows(xs.map((c) => ({ clientId: c.clientId, name: c.name, line: `Joined ${dayWords(c.joined)}`, amount: '' })));
  const part = (/** @type {string} */ h, /** @type {typeof ads} */ xs) => (xs.length ? `<section class="dv-drill-part"><h4>${escHtml(h)} <b>${xs.length}</b></h4>${list(xs)}</section>` : '');
  const lead = `You spent ${money(A.spend)} on ads from ${dayWords(A.from)} to ${dayWords(A.day)}. ${plural(ads.length, 'client', 'clients')} came from ads${A.paid != null ? `, so each one cost ${money(A.paid)}` : ''}.`
    + (A.blended != null ? ` Counting all ${plural(A.clients.length, 'new client', 'new clients')}, each one cost ${money(A.blended)}.` : '');
  return {
    sub: `${money(A.spend)} on ads, ${plural(ads.length, 'client', 'clients')} from them`,
    body: `<section class="dv-drill-part"><p>${escHtml(lead)}</p></section>${part('From ads', ads)}${part('Other new clients', other)}`,
    foot: A.paid != null ? sumRow('Each client from ads', money(A.paid)) : '',
  };
}

/**
 * A month's lifetime value, in its two halves: what a client pays a month (the money that came in over 12 months, by
 * person, against how many clients were paying), then the months clients stay (every client who left in 2 years).
 * @param {any} A the server's answer (_shared/dashboard/detail.js ltvDetail)
 */
function ltvBody(A) {
  const day = dayWords;
  const at = Math.max(1, Math.round(A.clientMonths / 12));
  const r = peopleRows(A.items);
  const stays = clientRows(/** @type {any[]} */ (A.stays).map((x) => ({ clientId: x.clientId, name: x.name, line: `${day(x.from)} to ${day(x.to)}`, amount: plural(x.months, 'month', 'months') })));
  // ⚠️ TWO TABS, one a half: under a long list of payers, the second half was out of sight (10/30)
  const body = `<div class="dv-drill-tabs" role="tablist" aria-label="The two halves of lifetime value">
      <button type="button" role="tab" aria-selected="true" data-drill-tab="0">Pays a month<b>${money(A.arpc)}</b></button>
      <button type="button" role="tab" aria-selected="false" data-drill-tab="1">Months they stay<b>${A.months}</b></button>
    </div>
    <section class="dv-drill-part" data-drill-pane="0">
      <h4>What a client pays a month <b>${money(A.arpc)}</b></h4>
      <p>${money(A.cash)} came in over 12 months, ${day(A.from)} to ${day(A.day)}, while about ${plural(at, 'client was', 'clients were')} paying at a time. That is ${money(A.arpc)} a client each month.</p>
      ${r.html}
    </section>
    <section class="dv-drill-part" data-drill-pane="1" hidden>
      <h4>How many months a client stays <b>${A.months}</b></h4>
      <p>The average stay of the ${plural(A.stays.length, 'client', 'clients')} who left in the 2 years to ${day(A.day)}, past clients from your Stripe history included.</p>
      ${stays}
    </section>`;
  return {
    sub: `${money(A.arpc)} a month, times ${A.months} months`, body,
    foot: sumRow('Lifetime value', money(A.ltv)),
  };
}

const TYPES = /** @type {const} */ ([['online', 'Online'], ['hybrid', 'Hybrid'], ['inPerson', 'In person']]);

/**
 * Clients on a day, by type, after who joined, came back, left and paused that month: the Active clients chart's
 * month end, and the Joined and left chart's month (the very same people its bars count).
 * @param {{ day: string, total: number, clients: any[], joined: any[], returned: any[], left: any[], paused: any[] }} A
 */
function activeBody(A) {
  const month = monthOf(A.day);
  const names = (/** @type {any[]} */ xs) => `<ul class="dv-drill-names">${[...xs].sort((a, b) => a.name.localeCompare(b.name)).map((p) => `<li>${av(p.name)}${who(p)}</li>`).join('')}</ul>`;
  const sec = (/** @type {string} */ h, /** @type {any[]} */ xs) => (xs.length ? `<section class="dv-drill-sec"><h4>${escHtml(h)}<span>${xs.length}</span></h4>${names(xs)}</section>` : '');
  const moves = /** @type {Array<[any[], string]>} */ ([[A.joined, 'joined'], [A.returned, 'came back'], [A.left, 'left'], [A.paused, 'paused']])
    .filter(([xs]) => xs.length).map(([xs, w]) => `${xs.length} ${w}`);
  const sub = moves.length ? `${andList(moves)} in ${month}` : `Nobody joined or left in ${month}`;
  const body = sec(`Joined in ${month}`, A.joined) + sec(`Came back in ${month}`, A.returned) + sec(`Left in ${month}`, A.left) + sec(`Paused in ${month}`, A.paused)
    + TYPES.map(([k, l]) => sec(l, A.clients.filter((c) => c.type === k))).join('');
  return { sub, body: body || '<p class="dv-drill-none">No paying clients on this day.</p>', foot: '' };
}

/** Each breakdown's card. @type {Record<Ask['what'], (A: any) => { sub: string, body: string, foot: string }>} */
const BODIES = { cash: cashBody, refunds: refundsBody, active: activeBody, mrr: mrrBody, ltv: ltvBody, renewals: renewalsBody, cac: cacBody };

/**
 * Open the card on what makes up a number.
 * @param {Ask} ask what to list @param {Head} head the drawer's heading: the card, the bar's date, its number
 */
export async function openDrill(ask, head) {
  const d = ensure();
  const mine = ++asked;
  if (!d.classList.contains('on')) { back = /** @type {HTMLElement|null} */ (document.activeElement); giveBack = byKey; }
  d.innerHTML = frame(head, '', '<p class="dv-drill-none" aria-busy="true">Loading…</p>');
  d.classList.add('on');
  document.getElementById('dv-drill-shade')?.classList.add('on');
  /** @type {HTMLElement|null} */ (d.querySelector('.dv-drill-x'))?.focus();
  try {
    const A = await callFunction('coach-dashboard-detail', null, 'GET', ask);
    if (mine !== asked) return; // closed, or another bar picked meanwhile
    const r = !A ? { sub: '', body: '<p class="dv-drill-none">Nothing to show for this month.</p>', foot: '' } : BODIES[ask.what](A);
    d.innerHTML = frame(head, r.sub, r.body, r.foot);
    /** @type {HTMLElement|null} */ (d.querySelector('.dv-drill-x'))?.focus();
  } catch (e) {
    if (mine !== asked) return;
    d.innerHTML = frame(head, '', `<p class="dv-drill-none" role="alert">This didn't load${e instanceof Error && e.message ? `: ${escHtml(e.message)}` : '.'} Close it and click again.</p>`);
  }
}
